import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { extend, get } from 'underscore';
import classNames from 'classnames';
import {
  Collapse,
  collapseExpander,
  useCollapseState,
} from '@bingads-webui-campaign/wizard-jumbo-card';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import { ValidationStringMap } from '@bingads-webui/validation-string-map';
import { DynamicSearchAd } from '@bingads-webui/campaign-odata-schemas';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { URLOptionsSection } from '@bingads-webui-campaign-react/validated-url-input';
import { ValidationGroup } from '@bingads-webui-react/validation';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { testUurl, uurlLevels } from '@bingads-webui-component-legacy/upgraded-url/service/testUurl';
import { DynamicSearchAdPreviewUAPL } from '@bingads-webui-campaign-react/dynamic-search-ad-preview-uapl';

import { AdText } from './ad-text';
import { getStyles } from './style';

export const DynamicSearchAdEditor = ({
  ad,
  onAdChanged,
  domain,
  permissions,
  scope,
  alwaysShowError,
}) => {
  const classes = useDefaultStyles(getStyles);
  const i18n = useContext(I18nContext);
  const legacyI18n = useMemo(() => buildLegacyI18n(i18n, [ValidationStringMap]), [i18n]);

  const serverContext = useContext(ServerContext);
  const { Token } = serverContext;

  const [cachedAd, setCachedAd] = useState(get(ad, 'data', {}));

  const {
    TrackingUrlTemplate, FinalUrlSuffix, UrlCustomParameters,
  } = cachedAd;

  const [
    allSections,
    expandedSections,
    setExpandedSections,
  ] = useCollapseState(
    ['ad-text', 'advanced-url-options'] /* all sections */,
    ['ad-text'] /* initially expanded sections */
  );

  const settingsExpand = useMemo(
    () => collapseExpander({ setActiveKeys: setExpandedSections }),
    [setExpandedSections]
  );

  const urlOptions = useMemo(() => ({
    TrackingUrlTemplate,
    FinalUrlSuffix,
    UrlCustomParameters,
  }), [FinalUrlSuffix, TrackingUrlTemplate, UrlCustomParameters]);

  const urlOptionsDetails = useRef(urlOptions);
  urlOptionsDetails.current = urlOptions;

  const triggerTestUurl = useCallback((fn, originalView) => {
    extend(originalView, {
      isInline: false,
      level: uurlLevels.Campaign,
    });
    const params = urlOptionsDetails.current;
    const options = {
      apiInLog: 'advancedSettingsUrlOptions/uurlTest',
    };
    testUurl(fn, originalView, params, scope, options);
  }, [scope]);

  const onValidate = useCallback((result) => {
    const validatedAd = { ...cachedAd, ...result.data };
    setCachedAd(validatedAd);
    onAdChanged({ ...result, data: validatedAd });
  }, [cachedAd, onAdChanged]);

  return (
    <div className={classNames('dynamic-search-ad-container', classes.container)}>
      <div className={classNames('dynamic-search-ad-editor', classes.editor)}>
        <ValidationGroup
          validationProps={{
            schema: DynamicSearchAd,
            onValidate,
            data: cachedAd,
            i18n: legacyI18n,
            alwaysShowError,
          }}
        >
          <Collapse allPanelItems={allSections} checkItems skipItemsTypeCheck activeKeys={expandedSections} setActiveKeys={setExpandedSections}>
            <AdText
              key="ad-text"
              expand={settingsExpand}
              i18n={i18n}
              data={cachedAd}
              domain={domain}
              permissions={permissions}
              scope={scope}
              onDataChange={onAdChanged}
            />
            <URLOptionsSection
              key="advanced-url-options"
              expand={settingsExpand}
              trackingUrlTemplate={TrackingUrlTemplate}
              finalUrlSuffix={FinalUrlSuffix}
              urlCustomParameters={UrlCustomParameters}
              token={Token}
              triggerTestUurl={triggerTestUurl}
            />
          </Collapse>
        </ValidationGroup>
      </div>
      <div className={classNames('dynamic-search-ad-preview', classes.preview)}>
        <DynamicSearchAdPreviewUAPL
          key="dynamic-search-ad"
          data={cachedAd}
          domain={domain}
          i18n={i18n}
        />
      </div>
    </div>
  );
};

DynamicSearchAdEditor.propTypes = {
  ad: PropTypes.shape({
    data: PropTypes.shape({}),
    isValid: PropTypes.bool,
  }),
  domain: PropTypes.string,
  onAdChanged: PropTypes.func.isRequired,
  permissions: PropTypes.shape({}).isRequired,
  scope: PropTypes.shape({
    customerId: PropTypes.string,
    accountId: PropTypes.string,
    campaignId: PropTypes.string,
    adGroupId: PropTypes.string,
  }).isRequired,
  alwaysShowError: PropTypes.bool,
};

DynamicSearchAdEditor.defaultProps = {
  ad: {
    data: {},
    isValid: false,
  },
  domain: '',
  alwaysShowError: false,
};
